import {
  Card,
  FormattedMessageDiv,
  LoadingAnimation,
  useWizardContext,
} from '@odmonorepo/core-components';
import { useEffect, useRef, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate } from '@tanstack/react-router';
import { submitAppOpts, UnifiedApiError, useApplication, useWaitForAppStatusChange } from 'merchant-api/unified';
import { getCaptchaHeaders, isCaptchaFailure } from 'merchant-api/vendor/google';
import { useAnalytics } from 'use-analytics';
import { useMutation } from '@tanstack/react-query';
import { steps } from '../LoanAppForm';

const DECISION_DEADLINE_MS = 180_000;

// The single submit parks the application on SUBMITTED while decisioning runs; polling the
// application is the source of truth for the outcome (decisionStatus on the submit response
// is best-effort only, so it is never used to advance).
const ADVANCE_STATUSES = ['PREPROCESSED', 'APPROVED'];
const DECLINE_STATUSES = ['NOT_APPROVED', 'CLOSED_LOST'];
const POST_SUBMIT_STATUSES = ['SUBMITTED', ...ADVANCE_STATUSES, ...DECLINE_STATUSES];

/**
 * Post-consent review screen: fires the single submit (the backend associates the user to the
 * application - find/create + ownership write - and kicks off decisioning in the same call),
 * then polls the application until the status leaves SUBMITTED. PREPROCESSED/APPROVED continue
 * to the step after CONSENT (nextStep); NOT_APPROVED/CLOSED_LOST land on DECLINED.
 *
 * When the submit answers with an OTP challenge the backend has NOT touched Butterfly (its
 * submit only accepts the owner's credential): the app stays pre-submit (STARTED /
 * SYSTEM_GENERATED) with consents already recorded. The user detours through /login/otp and
 * the mapper re-enters APP_REVIEW (pre-submit + consents places here), where the mount
 * re-fires the full submit as the now-authed owner. A refresh mid-decision instead comes back
 * SUBMITTED, which skips the submit and just resumes the poll.
 */
export const AppReviewCard = () => {
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const { nextStep, goToStep } = useWizardContext<typeof steps>();
  const { applicationData, isLoadingApplication } = useApplication();
  const applicationId = applicationData?.id;
  const [stuck, setStuck] = useState<Error>();
  const [done, setDone] = useState(false);
  const fired = useRef(false);
  const deadline = useRef(Date.now() + DECISION_DEADLINE_MS);

  const { mutate: submit, error: submitError, failureReason } = useMutation({
    ...submitAppOpts({ getHeaders: getCaptchaHeaders }),
    // One retry with a fresh captcha token. The new user was created before the Login Server
    // rejected the captcha, so the retry takes the existing-user path and returns otp_required.
    retry: (failureCount, error) => failureCount < 1 && isCaptchaFailure(error),
    throwOnError: false,
    onSuccess: (result) => {
      analytics.track('LoanAppReviewSubmitted', {
        decisionStatus: result.decisionStatus ?? result.applicationStatus,
      });
      if (result.auth?.action === 'otp_required') {
        // Return to this application after OTP so the wizard remounts APP_REVIEW with a user
        // token and resumes the decision poll (the app is SUBMITTED by then, so no re-submit).
        navigate({
          to: '/login/otp',
          search: { redirect: result.auth.redirect, emailMasked: result.auth.emailMasked, key: result.auth.key } as never,
        });
      }
    },
  });

  const { applicationData: polledApp } = useWaitForAppStatusChange(done ? undefined : applicationId);

  useEffect(() => {
    if (!applicationId || !applicationData?.status || fired.current) return;
    fired.current = true;
    // Re-entry safety (e.g. refresh mid-decision): already past submit -> only poll. Any
    // pre-submit status fires the submit, including the post-OTP return where the app is
    // still STARTED/SYSTEM_GENERATED - that re-submit IS the real Butterfly sequence.
    if (POST_SUBMIT_STATUSES.includes(applicationData.status)) return;
    submit({ applicationId });
  }, [applicationId, applicationData?.status]);

  useEffect(() => {
    const status = polledApp?.status;
    if (!status || done) return;
    if (ADVANCE_STATUSES.includes(status)) {
      setDone(true);
      nextStep();
    } else if (DECLINE_STATUSES.includes(status)) {
      setDone(true);
      goToStep('DECLINED');
    }
  }, [polledApp?.status, done]);

  useEffect(() => {
    if (done) return;
    const timer = setInterval(() => {
      if (Date.now() > deadline.current) {
        setStuck(failureReason ?? new Error('Timed out waiting for the application decision.'));
      }
    }, 3_000);
    return () => clearInterval(timer);
  }, [done, failureReason]);

  // A definitive rejection (4xx) will never produce a decision - fail fast instead of
  // waiting out the deadline. Timeouts/5xx/network errors keep polling. `error` is only set once
  // retries are exhausted (failureReason is set on every attempt), so a captcha failure still
  // gets its retry with a fresh token.
  if (submitError instanceof UnifiedApiError && submitError.status < 500) throw submitError;
  if (stuck) throw stuck;

  if (isLoadingApplication) {
    return <Stack><LoadingAnimation className="mx-auto mt-7" /></Stack>;
  }

  return (
    <Stack className="text-center mt-5" gap={6} style={{maxWidth: '600px', margin: 'auto'}}>
      <Stack gap={4}>
        <FormattedMessageDiv className="h3" id="renewalApp.review.title1" />
        <FormattedMessageDiv className="h5" id="renewalApp.review.title2" />
      </Stack>
      <Card bodyClassName='px-md-8'>
        <Stack gap={4}>
          <LoadingAnimation />
          <FormattedMessageDiv
            className="text-start"
            id="renewalApp.review.questions"
            values={{
              tel: (txt: any) => <a href={`tel:${txt}`}>{txt}</a>,
              email: (txt: any) => <a href= {`mailto:${txt}`}>{txt}</a>
            }}
          />
        </Stack>
      </Card>
    </Stack>
  );
};

export default AppReviewCard;
